import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, MessageSquare } from "lucide-react";
import { useLocation } from "wouter";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userName?: string;
  projectId?: string;
  module?: string;
}

const feedbackTypes = [
  { value: "Bug", label: "🐛 Bug Report" },
  { value: "Suggestion", label: "💡 Feature Suggestion" },
  { value: "Question", label: "❓ Question" },
  { value: "Other", label: "📝 Other" }
];

export function FeedbackModal({ 
  isOpen, 
  onClose, 
  userEmail, 
  userName, 
  projectId, 
  module 
}: FeedbackModalProps) {
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location] = useLocation();

  const submitFeedbackMutation = useMutation({
    mutationFn: async (feedbackData: any) => {
      const response = await apiRequest('POST', '/api/submit-feedback', feedbackData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "✅ Feedback submitted — thank you!",
        description: "We appreciate your input and will review it soon.",
      });
      resetForm();
      onClose();
      
      // Invalidate activity logs to show the feedback submission
      queryClient.invalidateQueries({ queryKey: ['/api/activity-logs'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to submit feedback",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const resetForm = () => {
    setFeedbackType("");
    setMessage("");
  };

  const handleSubmit = () => {
    if (!feedbackType || !message.trim()) {
      toast({
        title: "Please complete all fields",
        description: "Both feedback type and message are required.",
        variant: "destructive",
      });
      return;
    }

    const feedbackData = {
      user_id: userEmail || 'anonymous',
      user_email: userEmail,
      user_name: userName,
      project_id: projectId || null,
      module: module || getCurrentModule(),
      feedback_type: feedbackType,
      message: message.trim(),
      page_url: window.location.href
    };

    submitFeedbackMutation.mutate(feedbackData);
  };

  const getCurrentModule = () => {
    const path = location;
    if (path.includes('/maturity')) return 'Maturity Assessment';
    if (path.includes('/value')) return 'Value Engineering';
    if (path.includes('/benchmarking')) return 'Benchmarking';
    if (path.includes('/roi')) return 'ROI Analysis';
    if (path.includes('/admin')) return 'Admin Panel';
    return 'General';
  };

  const handleClose = () => {
    if (!submitFeedbackMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="feedback-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Share Your Feedback
          </DialogTitle>
        </DialogHeader>
        
        <div id="feedback-description" className="sr-only">
          Submit feedback to help us improve the platform. Choose a feedback type and describe your experience.
        </div>
        
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="feedback-type">Feedback Type</Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger>
                <SelectValue placeholder="Select feedback type..." />
              </SelectTrigger>
              <SelectContent>
                {feedbackTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Your Message</Label>
            <Textarea
              id="message"
              placeholder="Please describe your feedback, bug report, or suggestion in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Context Information Display */}
          <div className="text-xs text-muted-foreground bg-muted p-2 rounded border">
            <div className="font-medium mb-1">Context (automatically captured):</div>
            <div>Module: {module || getCurrentModule()}</div>
            <div>Page: {window.location.pathname}</div>
            {projectId && <div>Project: {projectId.slice(0, 8)}...</div>}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitFeedbackMutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitFeedbackMutation.isPending || !feedbackType || !message.trim()}
              className="flex-1"
            >
              {submitFeedbackMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}